import React, { useEffect, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import HubOutlinedIcon from '@mui/icons-material/HubOutlined';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import RuleOutlinedIcon from '@mui/icons-material/RuleOutlined';
import {
    BenchmarkRun, ComparisonDimension, DimensionType, DimensionsResponse, RunOutcome
} from '../../../services/benchmarkService';
import { NotComputed, PanelError, PanelSkeleton, SectionCard } from '../BenchmarkPrimitives';
import { count, humanise } from '../BenchmarkVocabulary';
import { WithheldIndex } from '../withheldMetrics';
import CoverageSummary from './CoverageSummary';
import DimensionDetail from './DimensionDetail';
import DimensionScorecard, { ScorecardRow } from './DimensionScorecard';
import OutcomeAssociationPanel from './OutcomeAssociationPanel';
import { RunScenariosContext } from './runScenarios';
import { agentLabelsFrom, agentSlugName, factorsByAgent } from './investigationVocabulary';
import { dsVar, ink } from '../../../theme/tokens';

/**
 * The tabs of an investigation run, each named for the question it answers.
 * A priority run's tabs (impact, business case) describe a re-ordered queue,
 * which an investigation run does not produce, so it gets its own set.
 */
export const INVESTIGATION_TABS = [
    { key: 'decisions', label: 'Decisions' },
    { key: 'agents', label: 'Agents & red flags' },
    { key: 'outcomes', label: 'Red flags vs outcome' },
    { key: 'typologies', label: 'Typologies' },
    { key: 'evidence', label: 'Evidence' }
] as const;

/** Where a run's cases entered, as a reader says it. */
export const STAGE_LABELS: Record<string, string> = {
    context: 'Alert enrichment (investigation contexts)',
    edd: 'Enhanced due diligence (EDD subjects)'
};

/**
 * The run's outcomes, the one its figures lead with first: an EDD review is judged by
 * whether the case was reported, so its SAR outcome leads there.
 */
export function orderedOutcomes(dimensions: DimensionsResponse | null): RunOutcome[] {
    const outcomes = dimensions?.outcomes ?? [];
    if (dimensions?.stage !== 'edd') return outcomes;
    return [...outcomes].sort((a, b) => Number(b.key === 'sar') - Number(a.key === 'sar'));
}

export type InvestigationTabKey = typeof INVESTIGATION_TABS[number]['key'];

/** The tab each kind of compared item is read on: a red flag sits under its agent. */
export const TAB_FOR_TYPE: Record<DimensionType, InvestigationTabKey> = {
    decision: 'decisions',
    agent: 'agents',
    factor: 'agents',
    typology: 'typologies'
};

/**
 * Scrolls an item's row or card into view; jsdom and old browsers have no scrollIntoView.
 * A scorecard row moves only as far as it must: selecting a row already in view leaves the page still.
 */
const scrollToItem = (dimensionKey: string, block: ScrollLogicalPosition) => {
    const el = document.querySelector(`[data-dimension-key="${dimensionKey}"]`) as HTMLElement | null;
    el?.scrollIntoView?.({ behavior: 'smooth', block });
};

interface InvestigationBenchmarkViewProps {
    run: BenchmarkRun;
    tab: InvestigationTabKey;
    dimensions: DimensionsResponse | null;
    loading: boolean;
    error?: string | null;
    onRetry: () => void;
    withheld: WithheldIndex;
    onCaseSelect: (caseRef: string) => void;
    selectedCaseRef?: string | null;
    /**
     * The item open on its tab: scrolled to, and on a scorecard tab the row marked as the
     * one the drawer shows. Set by a row, or by an item picked from Evidence → Coverage.
     */
    focusItem?: string | null;
    /**
     * Opens an item on its tab; the dashboard shows a scorecard's item in its drawer.
     * Without it, scorecard rows do not open and Evidence → Coverage lists items as plain text.
     */
    onOpenItem?: (tab: InvestigationTabKey, dimensionKey: string) => void;
}

const bodySx = { fontSize: '0.8125rem', color: dsVar('fg-soft'), lineHeight: 1.7 };

/**
 * The caveat that changes how every figure on the page reads: the institution's
 * side is a synthetic fixture drawn from the outcomes, so agreement with it is
 * planted. Stated once, above the tabs, rather than only in a badge tooltip.
 */
const PlantedSignalBanner: React.FC<{ run: BenchmarkRun }> = ({ run }) => {
    const planted = (run.withheldMetrics ?? []).find((w) => w.metric_family === 'planted_signal');
    if (!planted) return null;
    return (
        <Box sx={{
            p: 2, mb: 3, borderRadius: '10px', border: '1px solid rgba(251, 191, 36, 0.35)',
            backgroundColor: 'rgba(251, 191, 36, 0.06)', display: 'flex', gap: 1.5, alignItems: 'flex-start'
        }}>
            <WarningAmberOutlinedIcon sx={{ color: ink('#FBBF24'), fontSize: 20, mt: '1px' }} />
            <Box>
                <Typography sx={{ fontSize: '0.875rem', fontWeight: 700, color: ink('#FCD34D') }}>
                    The historical side of this comparison is synthetic
                </Typography>
                <Typography sx={{ fontSize: '0.8125rem', color: dsVar('fg-soft'), mt: 0.5, lineHeight: 1.6 }}>
                    {planted.reason} Use this run to check that the comparison works end to end, not to judge the agents.
                </Typography>
            </Box>
        </Box>
    );
};

/**
 * A tab's scorecard. A row opens its item in the dashboard's drawer, as a row does
 * on every other tab, rather than beneath the table where it landed out of sight.
 */
const ScorecardSection: React.FC<{
    title: string;
    subtitle: string;
    icon: React.ReactNode;
    intro?: React.ReactNode;
    rows: ScorecardRow[];
    showProducer?: boolean;
    emptyMessage: string;
    withheld: WithheldIndex;
    selectedKey?: string | null;
    onSelect: (dimensionKey: string) => void;
}> = ({ title, subtitle, icon, intro, rows, showProducer, emptyMessage, withheld, selectedKey, onSelect }) => (
    <SectionCard title={title} subtitle={subtitle} icon={icon} accent="#A855F7">
        {intro}
        <DimensionScorecard
            rows={rows}
            withheld={withheld}
            selectedKey={selectedKey}
            onSelect={onSelect}
            showProducer={showProducer}
            emptyMessage={emptyMessage}
        />
    </SectionCard>
);

/** What the run replayed and how, for a validator: the scope a claim is limited to. */
const RunScope: React.FC<{ run: BenchmarkRun; dimensions: DimensionsResponse }> = ({ run, dimensions }) => {
    const selection = dimensions.agentSelection ?? run.agentSelection;
    const labels = agentLabelsFrom(dimensions.dimensions ?? []);
    const name = (slug: string) => agentSlugName(slug, labels);
    const pins = (run.scorerConfig?.feature_pins ?? null) as Record<string, number | null> | null;
    const fields: [string, React.ReactNode][] = [
        ['Cases replayed', count(run.predictionCount)],
        ['Entered the workflow at', dimensions.stage ? STAGE_LABELS[dimensions.stage] ?? humanise(dimensions.stage) : '—'],
        ['Agents selected', selection?.agents?.length ? selection.agents.map(name).join(', ') : 'All agents'],
        ['Selection mode', selection?.mode === 'exact'
            ? 'Exact — each selected agent ran once, with no model routing'
            : selection?.mode === 'restrict' ? 'Restrict — the supervisor routed among the selected agents' : '—'],
        ['Not selected (not compared)', dimensions.unselectedAgents?.length ? dimensions.unselectedAgents.map(name).join(', ') : 'None'],
        ['Feature builds read', pins && Object.keys(pins).length
            ? Object.entries(pins).map(([epoch, v]) => `${epoch} → ${v ?? 'current'}`).join(' · ') : 'Current build'],
        ['Dataset version', run.datasetVersionId ?? '—'],
        ['Run', run.runId]
    ];
    if (selection?.unknown?.length) fields.push(['Selected but not recognised', selection.unknown.join(', ')]);
    if (dimensions.cohorts?.full_evidence) {
        fields.push(['Cases with every decision-driving agent', count(dimensions.cohorts.full_evidence)]);
    }
    return (
        <SectionCard title="What this run replayed" subtitle="The scope every figure on these tabs is limited to." icon={<FactCheckOutlinedIcon fontSize="small" />}>
            <Box className="grid gap-x-8 gap-y-3" sx={{ gridTemplateColumns: 'repeat(auto-fit, minmax(16rem, 1fr))' }}>
                {fields.map(([label, value]) => (
                    <Box key={label}>
                        <Typography sx={{ fontSize: '0.75rem', color: dsVar('fg-muted') }}>{label}</Typography>
                        <Typography sx={{ fontSize: '0.875rem', color: dsVar('fg-body'), mt: 0.25, wordBreak: 'break-word' }}>{value}</Typography>
                    </Box>
                ))}
            </Box>
        </SectionCard>
    );
};

/**
 * An investigation run's results: the platform's agents re-investigated cases
 * your team has closed, and each of their decisions, verdicts, red flags and
 * typologies is compared with the historical record.
 *
 * Everything is drawn from the run's manifest, so a run that compared other
 * decisions or agents renders the same way. Where a comparison could not be
 * made the page says why, and never shows the gap as agreement.
 */
const InvestigationBenchmarkView: React.FC<InvestigationBenchmarkViewProps> = ({
    run, tab, dimensions, loading, error, onRetry, withheld, onCaseSelect, selectedCaseRef, focusItem, onOpenItem
}) => {
    const dims = useMemo(() => dimensions?.dimensions ?? [], [dimensions]);
    useEffect(() => {
        if (focusItem) scrollToItem(focusItem, tab === 'decisions' ? 'center' : 'nearest');
    }, [focusItem, tab, dims]);
    const openOn = (onTab: InvestigationTabKey) => (dimensionKey: string) => onOpenItem?.(onTab, dimensionKey);
    const ofType = (type: string) => dims.filter((d) => d.dimensionType === type);

    const agentRows = useMemo<ScorecardRow[]>(() => {
        const factors = factorsByAgent(dims);
        return dims.filter((d) => d.dimensionType === 'agent').flatMap((agent) => [
            { dimension: agent },
            ...(factors.get(String(agent.producer)) ?? []).map((f) => ({ dimension: f, depth: 1 }))
        ]);
    }, [dims]);

    // Typologies either side named first: one neither side named on any case says nothing.
    const typologyRows = useMemo<ScorecardRow[]>(() => {
        const named = (d: ComparisonDimension) => (d.agreementCounts.both_flagged || 0)
            + (d.agreementCounts.platform_only || 0) + (d.agreementCounts.institution_only || 0);
        return dims.filter((d) => d.dimensionType === 'typology')
            .sort((a, b) => named(b) - named(a))
            .map((dimension) => ({ dimension }));
    }, [dims]);

    if (error) return <PanelError message={error} onRetry={onRetry} />;
    if (loading && !dimensions) return <PanelSkeleton height={420} />;
    if (!dimensions) return null;
    if (dimensions.availability !== 'computed') {
        return <NotComputed detail={dimensions.reason ?? 'This run recorded no comparisons.'} />;
    }

    const unselected = dimensions.unselectedAgents ?? [];
    const selection = dimensions.agentSelection ?? run.agentSelection;
    const labels = agentLabelsFrom(dims);
    const name = (slug: string) => agentSlugName(slug, labels);
    const outcomes = orderedOutcomes(dimensions);
    // The run's headline decision first (an EDD run: retain vs file SAR).
    const decisions = [...ofType('decision')].sort((a, b) =>
        Number(b.dimensionKey === `decision:${dimensions.headline}`) - Number(a.dimensionKey === `decision:${dimensions.headline}`));

    return (
        <RunScenariosContext.Provider value={dimensions.scenarios ?? null}>
            <Box>
                <PlantedSignalBanner run={run} />

                {tab === 'decisions' && (
                    <Box className="flex flex-col gap-6">
                        {decisions.length === 0 && <NotComputed detail="This run compared no decisions." />}
                        {decisions.map((d) => (
                            <Box key={d.dimensionKey} data-dimension-key={d.dimensionKey}>
                                <DimensionDetail
                                    runKey={run.runKey}
                                    summary={d}
                                    withheld={withheld}
                                    onCaseSelect={onCaseSelect}
                                    selectedCaseRef={selectedCaseRef}
                                    outcomes={outcomes}
                                />
                            </Box>
                        ))}
                    </Box>
                )}

                {tab === 'agents' && (
                    <ScorecardSection
                        title="Agents and their red flags"
                        subtitle="Each selected agent's overall verdict, then each red flag it produces, against the red flags in the historical record. Select a row to open it."
                        icon={<HubOutlinedIcon fontSize="small" />}
                        intro={(
                            <Typography sx={{ ...bodySx, mb: 2 }}>
                                {selection?.agents?.length
                                    ? `You selected ${selection.agents.map(name).join(', ')}${selection.mode ? ` (${selection.mode} mode)` : ''}.`
                                    : 'Every agent in the workflow was eligible.'}
                                {unselected.length > 0 && ` Not selected, so not compared: ${unselected.map(name).join(', ')}.`}
                                {' '}An agent compared on no case had nothing to read for these customers, or the historical record has no red flag it produces.
                            </Typography>
                        )}
                        rows={agentRows}
                        emptyMessage="This run compared no agents."
                        withheld={withheld}
                        selectedKey={focusItem}
                        onSelect={openOn('agents')}
                    />
                )}

                {tab === 'outcomes' && (
                    <OutcomeAssociationPanel
                        runKey={run.runKey}
                        preferredOutcome={outcomes[0]?.key ?? null}
                        cohorts={dimensions.cohorts}
                    />
                )}

                {tab === 'typologies' && (
                    <ScorecardSection
                        title="Typologies"
                        subtitle="Whether each typology the selected agents can evidence was named in the historical record and by the agent replay. The replay names a typology when a red flag that evidences it is raised. Select a row to open it."
                        icon={<CategoryOutlinedIcon fontSize="small" />}
                        rows={typologyRows}
                        showProducer
                        emptyMessage="None of the selected agents evidences a typology."
                        withheld={withheld}
                        selectedKey={focusItem}
                        onSelect={openOn('typologies')}
                    />
                )}

                {tab === 'evidence' && (
                    <Box className="flex flex-col gap-6">
                        <RunScope run={run} dimensions={dimensions} />

                        <SectionCard
                            title="Caveats this run attached"
                            subtitle="What the run declined to compare, or compared with a condition. Each shows as a Qualified badge on the figures it affects."
                            icon={<RuleOutlinedIcon fontSize="small" />}
                            accent="#FBBF24"
                        >
                            {(run.withheldMetrics ?? []).length === 0 ? (
                                <Typography sx={bodySx}>None. Every figure was computed without a condition.</Typography>
                            ) : (run.withheldMetrics ?? []).map((w) => (
                                <Box key={`${w.metric_family}-${w.reason}`} sx={{ py: 1, borderBottom: `1px solid ${dsVar('line')}` }}>
                                    <Typography sx={{ fontSize: '0.8125rem', fontWeight: 700, color: ink('#FCD34D') }}>{humanise(w.metric_family)}</Typography>
                                    <Typography sx={{ ...bodySx, color: dsVar('fg-2') }}>{w.reason}</Typography>
                                    {w.qualifies?.length ? (
                                        <Typography sx={{ fontSize: '0.6875rem', color: dsVar('fg-muted'), mt: 0.25 }}>Applies to: {w.qualifies.join(', ')}</Typography>
                                    ) : null}
                                </Box>
                            ))}
                        </SectionCard>

                        <SectionCard title="Coverage" subtitle="Whether the evidence is complete enough to judge this run by: what could be compared on every case, and what could not, with why." icon={<FactCheckOutlinedIcon fontSize="small" />}>
                            <CoverageSummary
                                dims={dims}
                                onOpenItem={onOpenItem ? (d) => onOpenItem(TAB_FOR_TYPE[d.dimensionType], d.dimensionKey) : undefined}
                            />
                        </SectionCard>

                        {(run.warnings ?? []).length > 0 && (
                            <SectionCard title="Run warnings" subtitle="What the run reported while preparing and scoring the replay." icon={<WarningAmberOutlinedIcon fontSize="small" />} accent="#FBBF24">
                                {(run.warnings ?? []).map((w) => (
                                    <Typography key={w} sx={{ ...bodySx, color: dsVar('fg-2'), py: 0.75, borderBottom: `1px solid ${dsVar('line')}` }}>{w}</Typography>
                                ))}
                            </SectionCard>
                        )}
                    </Box>
                )}
            </Box>
        </RunScenariosContext.Provider>
    );
};

export default InvestigationBenchmarkView;
